import { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useUpdateSiteMutation } from '../slices/sitesApiSlice';
import { toast } from 'react-toastify';

const AddSiteScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const [updateSite, { isLoading }] = useUpdateSiteMutation();
  
  useEffect(() => {
    if (!userInfo) {
      navigate('/login');
    }
  }, [userInfo, navigate]);

  const [site, setSite] = useState({
    name: '',
    abbr: '',
    company: '',
    locationAddress: {
      address: '',
      city: '',
      state: '',
      postalCode: '',
    },
    phoneNumber: '',
    contactName: '',
    contactNum: '',
    ipRange: '',
    ipRanges: [],
    mainFirewallIP: '',
    BGP: '',
    AS: '',
    DattoUID: '',
    lat: '',
    lng: '',
    files: [],
    images: [],
  });

  const onChange = (level) => (event) => {
    const { name, value } = event.target;
    
    if (!level) {
      setSite((prevSite) => ({
        ...prevSite,
        [name]: value,
      }));
    } else {
      setSite((prevSite) => ({
        ...prevSite,
        [level]: {
          ...prevSite[level],
          [name]: value,
        },
      }));
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    
    try {
      const res = await updateSite(site).unwrap();
      toast.success('Site created successfully');
      navigate(`/sites/${res.updatedSite._id}`);
    } catch (error) {
      toast.error(error?.data?.message || error.error);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Outfit:wght@300;400;500;600;700;800&display=swap');

        .modern-add-site-container {
          color: #ffffff;
          font-family: 'Outfit', 'Inter', sans-serif;
          max-width: 950px;
          margin: 0 auto;
          padding: 20px;
        }

        .back-btn {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #dfe6e9;
          padding: 8px 16px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 20px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          text-decoration: none !important;
        }

        .back-btn:hover {
          background: rgba(255, 255, 255, 0.12);
          border-color: rgba(255, 255, 255, 0.2);
          color: #ffffff !important;
          transform: translateX(-3px);
        }

        .page-header-block {
          margin-bottom: 25px;
        }

        .page-tag {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 2px;
          color: #00cec9;
          text-transform: uppercase;
          margin-bottom: 4px;
        }

        .page-title {
          font-size: 32px;
          font-weight: 700;
          background: var(--theme-gradient-light);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin: 0;
        }

        .page-subtitle {
          font-size: 14px;
          color: #b2bec3;
          margin: 5px 0 0 0;
        }

        .glass-form-card {
          background: rgba(30, 39, 46, 0.6);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 30px;
          box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.4);
        }

        .section-title {
          color: var(--theme-secondary);
          font-size: 15px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 20px;
        }

        .section-divider {
          border-top: 1px solid rgba(255, 255, 255, 0.08) !important;
          opacity: 1;
        }

        /* Form overrides */
        .modern-add-site-container .form-label {
          color: #dfe6e9;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }

        .modern-add-site-container .form-control,
        .modern-add-site-container .form-select {
          background: rgba(0, 0, 0, 0.25) !important;
          border: 1px solid rgba(255, 255, 255, 0.08) !important;
          color: #ffffff !important;
          border-radius: 8px !important;
          padding: 10px 14px !important;
          font-size: 14px !important;
          transition: all 0.2s ease !important;
        }

        .modern-add-site-container .form-control::placeholder {
          color: rgba(255, 255, 255, 0.3) !important;
        }

        .modern-add-site-container .form-control:focus,
        .modern-add-site-container .form-select:focus {
          background: rgba(0, 0, 0, 0.4) !important;
          border-color: var(--theme-secondary) !important;
          box-shadow: 0 0 0 3px rgba(235, 213, 112, 0.2) !important;
          outline: none !important;
        }

        .modern-add-site-container .form-select option {
          background-color: #2c3e50 !important;
          color: #ffffff !important;
        }

        .btn-submit {
          background: var(--theme-gradient) !important;
          border: none !important;
          border-radius: 10px !important;
          color: #ffffff !important;
          font-weight: 600 !important;
          padding: 12px 24px !important;
          box-shadow: 0 4px 15px rgba(184, 134, 11, 0.25) !important;
          transition: all 0.2s ease !important;
          width: 100%;
          margin-top: 15px;
        }

        .btn-submit:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 6px 20px rgba(184, 134, 11, 0.4) !important;
          opacity: 0.95;
        }

        .btn-submit:disabled {
          background: rgba(255, 255, 255, 0.1) !important;
          color: rgba(255, 255, 255, 0.3) !important;
          box-shadow: none !important;
          cursor: not-allowed;
          transform: none !important;
        }
      `}</style>

      <div className="modern-add-site-container">
        {/* Back navigation */}
        <Link to="/sites" className="back-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Sites
        </Link>

        {/* Page Header */}
        <div className="page-header-block">
          <div className="page-tag">Admin Tool</div>
          <h1 className="page-title">Register New Site</h1>
          <p className="page-subtitle">
            Add a new physical location, network configurations, and administration contact information.
          </p>
        </div>

        {/* Glassmorphic Form Card */}
        <div className="glass-form-card">
          <Form onSubmit={submitHandler}>
            {/* Section 1: General Details */}
            <div className="form-section mb-4">
              <h5 className="section-title">General Information</h5>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3" controlId="name">
                    <Form.Label>Site Name</Form.Label>
                    <Form.Control
                      name="name"
                      value={site.name}
                      onChange={onChange()}
                      type="text"
                      placeholder="e.g. Catholic Charities Main Office"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="abbr">
                    <Form.Label>Abbreviation</Form.Label>
                    <Form.Control
                      name="abbr"
                      value={site.abbr}
                      onChange={onChange()}
                      type="text"
                      placeholder="e.g. CC-MAIN"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="company">
                    <Form.Label>Company</Form.Label>
                    <Form.Select
                      name="company"
                      value={site.company}
                      onChange={onChange()}
                      required
                    >
                      <option value="" disabled>Select Company</option>
                      <option value="Catholic Charities">Catholic Charities</option>
                      <option value="Cemeteries">Cemeteries</option>
                      <option value="Diocese of Camden">Diocese of Camden</option>
                      <option value="Housing">Housing</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="phoneNumber">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      name="phoneNumber"
                      value={site.phoneNumber}
                      onChange={onChange()}
                      type="text"
                      placeholder="e.g. 856-123-4567"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="contactName">
                        <Form.Label>Contact Name</Form.Label>
                        <Form.Control
                          name="contactName"
                          value={site.contactName}
                          onChange={onChange()}
                          type="text"
                          placeholder="Primary contact"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="contactNum">
                        <Form.Label>Contact Phone</Form.Label>
                        <Form.Control
                          name="contactNum"
                          value={site.contactNum}
                          onChange={onChange()}
                          type="text"
                          placeholder="Phone number"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>

            <hr className="section-divider my-4" />

            {/* Section 2: Location details */}
            <div className="form-section mb-4">
              <h5 className="section-title">Location & Coordinates</h5>
              <Row>
                <Col md={5}>
                  <Form.Group className="mb-3" controlId="address">
                    <Form.Label>Street Address</Form.Label>
                    <Form.Control
                      name="address"
                      value={site.locationAddress.address}
                      onChange={onChange('locationAddress')}
                      type="text"
                      placeholder="123 Main St"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3" controlId="city">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      name="city"
                      value={site.locationAddress.city}
                      onChange={onChange('locationAddress')}
                      type="text"
                      placeholder="City"
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3" controlId="state">
                    <Form.Label>State</Form.Label>
                    <Form.Control
                      name="state"
                      value={site.locationAddress.state}
                      onChange={onChange('locationAddress')}
                      type="text"
                      placeholder="State"
                    />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3" controlId="postalCode">
                    <Form.Label>Postal Code</Form.Label>
                    <Form.Control
                      name="postalCode"
                      value={site.locationAddress.postalCode}
                      onChange={onChange('locationAddress')}
                      type="text"
                      placeholder="Zip Code"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="lat">
                    <Form.Label>Latitude</Form.Label>
                    <Form.Control
                      name="lat"
                      value={site.lat}
                      onChange={onChange()}
                      type="text"
                      placeholder="e.g. 39.64164"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="lng">
                    <Form.Label>Longitude</Form.Label>
                    <Form.Control
                      name="lng"
                      value={site.lng}
                      onChange={onChange()}
                      type="text"
                      placeholder="e.g. -74.79874"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <hr className="section-divider my-4" />

            {/* Section 3: Network Settings */}
            <div className="form-section mb-4">
              <h5 className="section-title">Network Configuration</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="mainFirewallIP">
                    <Form.Label>Main Firewall IP</Form.Label>
                    <Form.Control
                      name="mainFirewallIP"
                      value={site.mainFirewallIP}
                      onChange={onChange()}
                      type="text"
                      placeholder="e.g. 192.168.1.1"
                    />
                  </Form.Group>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="BGP">
                        <Form.Label>BGP Config</Form.Label>
                        <Form.Control
                          name="BGP"
                          value={site.BGP}
                          onChange={onChange()}
                          type="text"
                          placeholder="e.g. Enabled"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="AS">
                        <Form.Label>AS Number</Form.Label>
                        <Form.Control
                          name="AS"
                          value={site.AS}
                          onChange={onChange()}
                          type="text"
                          placeholder="e.g. 65001"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3" controlId="DattoUID">
                    <Form.Label>Datto UID</Form.Label>
                    <Form.Control
                      name="DattoUID"
                      value={site.DattoUID || ''}
                      onChange={onChange()}
                      type="text"
                      placeholder="e.g. 12345"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="ipRange">
                    <Form.Label>IP Ranges (CIDR / list)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="ipRange"
                      value={site.ipRange}
                      onChange={onChange()}
                      placeholder="e.g.&#10;192.168.1.0/24&#10;10.0.0.0/16"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <Button type="submit" className="btn-submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create Site'}
            </Button>
          </Form>
        </div>
      </div>
    </>
  );
};

export default AddSiteScreen;
